import React from "react";
import { Link } from "react-router-dom";
import { FaTelegramPlane, FaTwitter, FaLinkedinIn, FaMediumM, FaNewspaper, FaGithub } from "react-icons/fa";
import { HiMail } from "react-icons/hi";
import logo from "../../assets/img/logo.png";
import { useTheme } from "../ThemeProvider";
import { motion } from "framer-motion";
import fuelLogo from "../../assets/img/compliance/fuel-logo.png";
import gdprLogo from "../../assets/img/compliance/gdpr-logo.png";
import nistLogo from "../../assets/img/compliance/nist-logo.png";
import owaspLogo from "../../assets/img/compliance/owasp-logo.png";

const scrollPageToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const FooterLink = ({ to, children, className = "", ...props }) => (
  <Link
    to={to}
    className={className}
    onClick={() => {
      scrollPageToTop();
      requestAnimationFrame(scrollPageToTop);
    }}
    {...props}
  >
    {children}
  </Link>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { isDark } = useTheme();
  
  const socialLinks = [
    {
      icon: <FaTwitter />,
      href: "https://twitter.com/SafeEdges",
      label: "Twitter",
      color: "hover:bg-blue-400"
    },
    {
      icon: <FaLinkedinIn />,
      href: "https://www.linkedin.com/company/safe-edges",
      label: "LinkedIn",
      color: "hover:bg-blue-600"
    },
    {
      icon: <FaTelegramPlane />,
      href: "https://t.me/bugtester25",
      label: "Telegram",
      color: "hover:bg-blue-500"
    },
    {
      icon: <FaMediumM />,
      href: "https://safe-edges.medium.com/",
      label: "Medium",
      color: "hover:bg-green-600"
    },
    // {
    //   icon: <FaGithub />,
    //   href: "https://github.com/safe-edges",
    //   label: "Github",
    //   color: "hover:bg-gray-600"
    // }
  ];

  const handleNewsletterSubscribe = () => {
    window.open("https://www.linkedin.com/newsletters/blockchain-security-watch-7172665953619591170/", "_blank");
  };

  return (
    <footer className="bg-black border-t border-gray-800 relative z-10 overflow-visible">
      <div className="container mx-auto px-6 py-10 pb-16">
        <div className="grid gap-10 lg:grid-cols-4">
          {/* Company + Contact */}
          <div className="space-y-6">
            <div>
            <FooterLink to="/" className="inline-block mb-4" aria-label="Safe Edges homepage">
              <motion.img 
                src={logo} 
                alt="Safe Edges Logo"
                width="48" height="48"
                className="h-12 w-auto"
                  loading="lazy"
                  decoding="async"
                whileHover={{ scale: 1.05 }}
              />
            </FooterLink>
            <p className="text-sm text-gray-400 mb-4">
              Premier blockchain and cybersecurity firm dedicated to fortifying digital assets.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  whileHover={{ scale: 1.1 }}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 rounded-full bg-black ${social.color} flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-blue-500/20`}
                  aria-label={social.label}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </div>

            <div className="space-y-3">
              <h3 className="text-cyan-400 font-medium">Contact</h3>
            <motion.a
              whileHover={{ scale: 1.02 }}
              href="mailto:info@safeedges.in"
                className="flex items-center text-sm text-gray-400 hover:text-cyan-400"
            >
              <HiMail className="mr-2" />
              info@safeedges.in
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.02 }}
              href="https://calendly.com/vivanchauhan-safeedges/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-gray-400 hover:text-cyan-400"
            >
              <FaTelegramPlane className="mr-2" />
              Schedule Meeting
            </motion.a>
            </div>
          </div>

          {/* Quick + Legal + Newsletter */}
          <div className="space-y-6">
            <div>
              <h3 className="text-cyan-400 font-medium mb-3">Quick Links</h3>
              <ul className="space-y-2">
                {[
                  { to: '/about-us', label: 'About Us' },
                  { to: '/team', label: 'Our Team' },
                  { to: '/services', label: 'All Services' },
                  { to: '/industries', label: 'By Industry' },
                  { to: '/locations', label: 'Where We Work' },
                  { to: '/smart-contract-audit-cost', label: 'Audit Pricing' },
                  { to: '/audit-process', label: 'Audit Process' },
                  { to: '/tools', label: 'Free Tools' },
                  { to: '/products/bastion-security', label: 'Bastion Security' },
                  { to: '/reports', label: 'Audit Reports' },
                  { to: '/faq', label: 'FAQ' },
                ].map((l) => (
                  <li key={l.to}>
                    <FooterLink to={l.to} className="text-sm text-gray-400 hover:text-cyan-400">
                      {l.label}
                    </FooterLink>
                  </li>
                ))}
                {/* Sitewide link so link equity flows to the content hub; without it the
                    blog subdomain is an island no crawler reaches from the main site. */}
                <li>
                  <a href="https://blog.safeedges.in/" className="text-sm text-gray-400 hover:text-cyan-400">
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-cyan-400 font-medium mb-3">Legal</h3>
              <ul className="space-y-2">
                <li><FooterLink to="/privacy-policy" className="text-sm text-gray-400 hover:text-cyan-400">Privacy Policy</FooterLink></li>
                <li><FooterLink to="/terms-of-use" className="text-sm text-gray-400 hover:text-cyan-400">Terms of Use</FooterLink></li>
                <li>
                  <a href="https://safeedges.in/SafeEdgesbrandkit/" className="text-sm text-gray-400 hover:text-cyan-400">
                    Brand Assets
                  </a>
                </li>
              </ul>
            </div>

            <div className="space-y-3">
              <h3 className="text-cyan-400 font-medium">Newsletter</h3>
              <p className="text-sm text-gray-400">
                Stay updated with blockchain security insights and latest threats.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNewsletterSubscribe}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-cyan-500/25"
              >
                <FaNewspaper />
                Subscribe Now
              </motion.button>
            </div>
          </div>

          {/* Services + Languages */}
          <div className="space-y-6">
            <div>
              <h3 className="text-cyan-400 font-medium mb-3">All Services</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-300">
                {[
                  { label: "Smart Contract Audit", slug: "smart-contract-audit" },
                  { label: "Wallet Audit", slug: "wallet-audit" },
                  { label: "Blockchain Protocol Audit", slug: "blockchain-protocol-audit"},
                  { label: "vCISO", slug: "vciso" },
                  { label: "DeFAI Audit", slug: "defai-audit" },
                  { label: "DeFi Diligence", slug: "defi-diligence" },
                  { label: "dApp Audit", slug: "dapp-audit" },
                  { label: "Pen Test", slug: "pen-test" },
                  { label: "RWA Audit", slug: "rwa-audit" },
                  { label: "DePin Audit", slug: "depin-audit" },
                  { label: "GameFi Audit", slug: "gamefi-audit" },
                  { label: "AVS Audit", slug: "avs-audit" },
                  { label: "Blockchain Forensics", slug: "blockchain-forensics" },
                  { label: "NFT Due Diligence", slug: "nft-due-diligence" },
                  { label: "Stablecoin Audit", slug: "stablecoin-audit" },
                  { label: "Bridge Audit", slug: "bridge-audit" },
                  { label: "Perp DEX Audit", slug: "perp-dex-audit" },
                  { label: "Incident Response", slug: "incident-response" },
                  { label: "AI & MCP Audit", slug: "ai-mcp-audit" },
                  { label: "Lending Audit", slug: "lending-audit" },
                  { label: "Enterprise DLT Audit", slug: "blockchain-dlt-audit" },
                  { label: "Blockchain Security Audit", slug: "blockchain-security-audit" },
                  { label: "Blockchain Security Assessment", slug: "blockchain-security-assessment" },
                  { label: "Vulnerability Assessment", slug: "blockchain-vulnerability-assessment" },
                  { label: "Infrastructure Security Audit", slug: "blockchain-infrastructure-security-audit" },
                  { label: "Layer 2 Security Audit", slug: "layer-2-security-audit" },
                  { label: "Tokenized Securities Audit", slug: "tokenized-securities-audit" },
                ].map((item) => (
                  <FooterLink
                    key={item.slug}
                    to={`/services/${item.slug}`}
                    className="hover:text-cyan-400 transition-colors duration-200"
                  >
                    {item.label}
                  </FooterLink>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-cyan-400 font-medium mb-3">Languages</h3>
              <div className="flex flex-wrap gap-2 text-sm text-gray-400">
                {[
                  { label: "Solidity Audit", slug: "solidity-audit" },
                  { label: "Rust Audit", slug: "rust-audit" },
                  { label: "Move Audit", slug: "move-audit" },
                  { label: "Cairo Audit", slug: "cairo-audit" },
                  { label: "Clarity Audit", slug: "clarity-audit" },
                  { label: "Sway Audit", slug: "sway-audit" },
                  { label: "Noir Audit", slug: "noir-audit" },
                  { label: "Cadence Audit", slug: "cadence-audit" },
                  { label: "Go Audit", slug: "go-audit" },
                ].map((item) => (
                  <FooterLink
                    key={item.slug}
                    to={`/services/lang/${item.slug}`}
                    className="px-3 py-1 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400 hover:text-cyan-300 transition-colors duration-200"
                  >
                    {item.label}
                  </FooterLink>
                ))}
              </div>
            </div>

          </div>

          {/* Chains */}
          <div className="space-y-3">
            <h3 className="text-cyan-400 font-medium">Chains</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-400">
              {[
              { label: "Ethereum (EVM) Audit", slug: "ethereum-audit" },
              { label: "Solana Audit", slug: "solana-audit" },
              { label: "Base Chain Audit", slug: "base-chain-audit" },
              { label: "Arbitrum Audit", slug: "arbitrum-audit" },
              { label: "Sui Audit", slug: "sui-audit" },
              { label: "Starknet Audit", slug: "starknet-audit" },
              { label: "Berachain Audit", slug: "berachain-audit" },
              { label: "Polygon Audit", slug: "polygon-audit" },
              { label: "zkSync Audit", slug: "zksync-audit" },
              { label: "BSC Audit", slug: "bsc-audit" },
              { label: "Near Audit", slug: "near-audit" },
              { label: "Cosmos Audit", slug: "cosmos-audit" },
              { label: "ZetaChain Audit", slug: "zetachain-audit" },
              { label: "Avalanche Audit", slug: "avalanche-audit" },
              { label: "HyperEVM Audit", slug: "hyperevm-audit" },
              { label: "HL Fabric Audit", slug: "hl-fabric-audit" },
              { label: "Polkadot Audit", slug: "polkadot-audit" },
              { label: "Stacks Audit", slug: "stacks-audit" },
              { label: "Tezos Audit", slug: "tezos-audit" },
              { label: "Blast Chain Audit", slug: "blast-chain-audit" },
              { label: "Optimism Audit", slug: "optimism-audit" },
              { label: "Linea Audit", slug: "linea-audit" },
              { label: "Flow Audit", slug: "flow-audit" },
              { label: "Xion Audit", slug: "xion-audit" },
              { label: "Scroll Audit", slug: "scroll-audit" },
              { label: "RISC Zero Audit", slug: "risc-zero-audit" },
              { label: "Morph Audit", slug: "morph-audit" },
              { label: "Aztec Audit", slug: "aztec-audit" },
              { label: "Monad Audit", slug: "monad-audit" },
              { label: "Canton Audit", slug: "canton-audit" },
            ].map((item) => (
              <FooterLink
                key={item.slug}
                to={`/services/chain/${item.slug}`}
                className="hover:text-cyan-400 transition-colors duration-200"
              >
                {item.label}
              </FooterLink>
            ))}
            </div>
          </div>
        </div>

        {/* Compliance Badges */}
        <div className="border-t border-gray-800 mt-10 pt-8">
          <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-5">Trusted &amp; Recognized</p>
          <div className="flex flex-wrap items-center gap-3">
            {[
              { logo: fuelLogo, name: "Fuel Chain", label: "Official Security Partner" },
              { logo: gdprLogo, name: "GDPR", label: "EU Data Protection" },
              { logo: nistLogo, name: "NIST", label: "Cybersecurity Framework" },
              { logo: owaspLogo, name: "OWASP", label: "App Security Standards" },
            ].map((badge) => (
              <div
                key={badge.name}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-white/8 bg-white/[0.03] hover:border-teal-500/30 transition-colors duration-200"
                title={badge.label}
              >
                <img
                  src={badge.logo}
                  alt={badge.name}
                  width={28}
                  height={28}
                  className="w-7 h-7 object-contain opacity-80"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <p className="text-xs font-semibold text-white leading-none">{badge.name}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5 leading-none">{badge.label}</p>
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-teal-500/20 bg-teal-500/5">
              <div className="w-7 h-7 rounded-md bg-teal-500/15 flex items-center justify-center text-teal-400 font-bold text-sm">✓</div>
              <div>
                <p className="text-xs font-semibold text-teal-400 leading-none">100% Track Record</p>
                <p className="text-[10px] text-gray-500 mt-0.5 leading-none">Zero Hacks on Our Watch</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col md:flex-row justify-between items-center gap-2">
          <p className="text-sm text-gray-400 mb-4 md:mb-0">
            © {currentYear} Safe Edges. All rights reserved.
          </p>
          <a
            href="https://thesignal.directory/safe-edges"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Verified by THE SIGNAL"
            className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-400 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-green-500/80" aria-hidden="true">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <span>
              Verified by <strong>THE SIGNAL</strong>
            </span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;